import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleArrowDown,
  faCircleArrowUp,
  faEllipsisVertical,
} from '@fortawesome/pro-regular-svg-icons';
import { Button, Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { SocketContext } from '../../../../socketProvider';
import { inRange, socketEventNames, MAX_ZOOM } from '../../helper';
import { updatePath, updatePaths } from '../../actions';
import { confirmationDialog, displayErrorModal, displaySuccessModal } from '../../../../helpers/swal';
import { post } from '../../../../helpers/fetch';
import { buildURL } from '../../../../helpers/urls';
import camMap, { fetchCamMapJson, listMappingCams } from '../../mapLogic';
import { MAX_SCAN_TILES } from '../../scanBoundsFromSeats';
import { fetchZoomCalibration } from '../../zoomCalibration';

import style from './style.module.scss';

const SCAN_REGION_TIMEOUT_MS = 10 * 60 * 1000;
const TRIGGER_URL = buildURL('crowd-count/triggerCrowdCountSession');

const toTriggerRegion = (region) => ({
  regionId: region.regionId,
  ...(region.name ? { name: region.name } : {}),
  start: { pan: region.startPan, tilt: region.startTilt },
  end: { pan: region.endPan, tilt: region.endTilt },
  zoom: region.zoom,
});

/** Production (queue) or reference (immediate publish) — both adminAuth HTTP. */
const triggerCrowdCount = (camera, regionList = [], { isReferenceScan = false, slugDate } = {}) => {
  if (!camera) {
    return post(TRIGGER_URL, {});
  }
  return post(TRIGGER_URL, {
    camera,
    regions: regionList.map(toTriggerRegion),
    ...(isReferenceScan ? { isReferenceScan: true, slugDate } : {}),
  });
};

const {
  ROVER_MOVE,
  ROVER_GET_POSITION,
  ROVER_JOIN_LIVESTREAM,
  ROVER_LEAVE_LIVESTREAM,
  SCAN_REGION_DONE,
} = socketEventNames;

const GRID_FORM_CLEAR_PATHS = {
  'grid.startPoint.pan': '',
  'grid.startPoint.tilt': '',
  'grid.endPoint.pan': '',
  'grid.endPoint.tilt': '',
  'grid.regionName': '',
};

const GRID_DISCONNECT_CLEAR_PATHS = {
  ...GRID_FORM_CLEAR_PATHS,
  'grid.regions': [],
  'grid.editingRegionId': null,
  'grid.viewingRegionId': null,
};

const MOSAIC_DIM_KEYS = ['rows', 'cols', 'tileWidth', 'tileHeight'];

const mosaicDimsFromPayload = (payload = {}) => {
  const dims = {};
  MOSAIC_DIM_KEYS.forEach((key) => {
    const n = Number(payload[key]);
    if (n > 0) dims[key] = n;
  });
  return dims;
};

const uiRegionsFromFile = (file) => (file?.scan?.regions || [])
  .map(camMap.scanRegionFromFile)
  .filter((region) => region?.regionId);

const GridPoint = ({ label, path, disabled }) => {
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const update = useCallback((...args) => dispatch(updatePath(...args)), [dispatch]);
  const pan = useSelector(({ mapping: { grid } }) => grid[path].pan);
  const tilt = useSelector(({ mapping: { grid } }) => grid[path].tilt);
  const zoom = useSelector(({ mapping: { preview: { zoom: { value } } } }) => value);
  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);

  const requestGetPosition = () => {
    if (disabled) return;
    dispatch(updatePath('positionPath', `grid.${path}`));
    socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#');
  };

  const sendPositionToCamera = () => {
    if (disabled) return;
    const safePan = Number(pan);
    const safeTilt = Number(tilt);
    const safeZoom = Number(zoom / 1000);

    if (connectedCamera && Number.isInteger(safePan)
        && Number.isInteger(safeTilt) && inRange(safeZoom, 0, MAX_ZOOM)) {
      socket.emit(ROVER_MOVE, 'host', connectedCamera, `!${safePan},${safeTilt},${safeZoom}`);
    }
  };

  return (
    <div className={style.block}>
      <div className={style.top}>
        <span>{label}</span>
        <div>
          <button
            className={style.circleButton}
            onClick={requestGetPosition}
            type="button"
            title="Get position from camera"
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faCircleArrowDown} />
          </button>
          <button
            className={style.circleButton}
            onClick={sendPositionToCamera}
            type="button"
            title="Send position to camera"
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faCircleArrowUp} />
          </button>
        </div>
      </div>
      <div className={style.row}>
        <span>Pan</span>
        <input
          className={style.input}
          type="number"
          value={pan}
          onChange={(e) => update(`grid.${path}.pan`, e.target.value)}
        />
      </div>
      <div className={style.row}>
        <span>Tilt</span>
        <input
          className={style.input}
          type="number"
          value={tilt}
          onChange={(e) => update(`grid.${path}.tilt`, e.target.value)}
        />
      </div>
    </div>
  );
};

const RegionSummary = ({ region }) => (
  <dl className={style.regionDetails}>
    <div className={style.regionDetailRow}>
      <dt>Label:</dt>
      <dd>{region.name}</dd>
    </div>
    <div className={style.regionDetailRow}>
      <dt>Start:</dt>
      <dd>{`(${region.startPan}, ${region.startTilt})`}</dd>
    </div>
    <div className={style.regionDetailRow}>
      <dt>End:</dt>
      <dd>{`(${region.endPan}, ${region.endTilt})`}</dd>
    </div>
    <div className={style.regionDetailRow}>
      <dt>Zoom:</dt>
      <dd>{region.zoom}</dd>
    </div>
  </dl>
);

const RegionActionsDropdown = ({
  region,
  cameraConnected,
  viewingRegionId,
  runningRegionId,
  busy,
  onRun,
  onView,
  onEdit,
  onDelete,
}) => (
  <Dropdown className={style.regionDropdown}>
    <Dropdown.Toggle variant="link" className={style.ellipsisToggle}>
      <FontAwesomeIcon icon={faEllipsisVertical} />
    </Dropdown.Toggle>
    {createPortal(
      <Dropdown.Menu alignRight className={style.regionMenu}>
        <Dropdown.Item
          disabled={!cameraConnected || busy}
          onClick={() => onRun(region)}
        >
          {runningRegionId === region.regionId ? 'Running…' : 'Run'}
        </Dropdown.Item>
        <Dropdown.Item
          disabled={busy}
          onClick={() => onRun(region, true)}
        >
          {runningRegionId === region.regionId ? 'Running…' : 'Run Production'}
        </Dropdown.Item>
        <Dropdown.Item onClick={() => onView(region)}>
          {viewingRegionId === region.regionId ? 'Viewing' : 'View'}
        </Dropdown.Item>
        <Dropdown.Item onClick={() => onEdit(region)}>Edit</Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item className="text-danger" onClick={() => onDelete(region.regionId)}>
          Delete
        </Dropdown.Item>
      </Dropdown.Menu>,
      document.body,
    )}
  </Dropdown>
);

const Grid = () => {
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const [runningByCamera, setRunningByCamera] = useState({});
  const [allCamsMode, setAllCamsMode] = useState(null);
  const update = useCallback((...args) => dispatch(updatePath(...args)), [dispatch]);
  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);
  const loadingStatus = useSelector(({ mapping: { loadingStatus: status } }) => status);
  const startPan = useSelector(({ mapping: { grid } }) => grid.startPoint.pan);
  const startTilt = useSelector(({ mapping: { grid } }) => grid.startPoint.tilt);
  const endPan = useSelector(({ mapping: { grid } }) => grid.endPoint.pan);
  const endTilt = useSelector(({ mapping: { grid } }) => grid.endPoint.tilt);
  const zoom = useSelector(({ mapping: { preview: { zoom: { value } } } }) => value);
  const regions = useSelector(({ mapping: { grid } }) => grid.regions);
  const regionName = useSelector(({ mapping: { grid } }) => grid.regionName);
  const editingRegionId = useSelector(({ mapping: { grid } }) => grid.editingRegionId);
  const viewingRegionId = useSelector(({ mapping: { grid } }) => grid.viewingRegionId);
  const slugDate = useSelector(({ events }) => events.currentEvent?.slugDate);
  const pausedForGridRef = useRef(false);
  const runningRegionId = runningByCamera[connectedCamera] ?? null;
  const anyRunning = runningRegionId !== null || Boolean(allCamsMode);

  const refreshRegions = useCallback(() => {
    update('grid.regions', camMap.getScanRegionsForUi());
  }, [update]);

  /** Write production mosaic dims onto the mapping region so Arrange can size blocks. */
  const persistProductionMosaicDims = useCallback(async (payload = {}) => {
    if (payload?.isReferenceScan === true || payload?.error) return;
    if (payload?.captureComplete !== true) return;
    const { camera, regionId } = payload;
    if (!camera || !regionId) return;

    const dims = mosaicDimsFromPayload(payload);
    if (!Object.keys(dims).length) return;

    try {
      const file = await fetchCamMapJson(camera);
      const existing = uiRegionsFromFile(file).find((region) => region.regionId === regionId);
      if (!existing) return;
      const unchanged = Object.keys(dims).every(
        (key) => Number(existing[key]) === Number(dims[key]),
      );
      if (unchanged) return;
      await camMap.updateScanRegionForCamera(camera, regionId, { ...existing, ...dims });
      if (camera === connectedCamera) refreshRegions();
    } catch (err) {
      console.warn('[Grid] failed to persist production mosaic dims', err);
    }
  }, [connectedCamera, refreshRegions]);

  // Persist Arrange extents from any production capture_complete (dims on socket).
  useEffect(() => {
    if (!socket) return undefined;
    const onDone = (payload = {}) => {
      persistProductionMosaicDims(payload);
    };
    socket.on(SCAN_REGION_DONE, onDone);
    return () => socket.off(SCAN_REGION_DONE, onDone);
  }, [socket, persistProductionMosaicDims]);

  useEffect(() => {
    if (!connectedCamera) {
      dispatch(updatePaths(GRID_DISCONNECT_CLEAR_PATHS));
      return;
    }
    refreshRegions();
  }, [connectedCamera, loadingStatus, dispatch, refreshRegions]);

  // Leave livestream in grid view; resume only while still mounted (not on page unmount).
  useEffect(() => {
    if (!connectedCamera || !socket) {
      pausedForGridRef.current = false;
      return undefined;
    }

    if (viewingRegionId !== null) {
      socket.emit(ROVER_LEAVE_LIVESTREAM, 'map', connectedCamera);
      pausedForGridRef.current = true;
      return undefined;
    }

    if (pausedForGridRef.current) {
      pausedForGridRef.current = false;
      socket.emit(ROVER_JOIN_LIVESTREAM, 'map', connectedCamera, (joined) => {
        if (joined) {
          socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#');
        }
      });
    }

    return undefined;
  }, [connectedCamera, viewingRegionId, socket]);

  const clearForm = () => {
    dispatch(updatePaths(GRID_FORM_CLEAR_PATHS));
  };

  const ensureCameraConnected = () => {
    if (connectedCamera) return true;
    displayErrorModal({ text: 'Connect a camera to capture points.' });
    return false;
  };

  const buildRegionFromForm = () => ({
    name: regionName.trim(),
    startPan: Number(startPan),
    startTilt: Number(startTilt),
    endPan: Number(endPan),
    endTilt: Number(endTilt),
    zoom: Number(zoom) / 1000,
  });

  const isPointsValid = () => !isEmpty(startPan.toString()) && !isEmpty(startTilt.toString())
      && !isEmpty(endPan.toString()) && !isEmpty(endTilt.toString());

  const validateRegionForm = (excludeId = null) => {
    if (!ensureCameraConnected()) return false;
    if (!regionName.trim()) {
      displayErrorModal({ text: 'Region name is required.' });
      return false;
    }
    if (!isPointsValid()) {
      displayErrorModal({ text: 'Start and end pan/tilt values are required.' });
      return false;
    }
    const nameTaken = regions.some((region) => (
      region.regionId !== excludeId
      && region.name?.trim().toLowerCase() === regionName.trim().toLowerCase()
    ));
    if (nameTaken) {
      displayErrorModal({ text: 'A region with this name already exists.' });
      return false;
    }
    return true;
  };

  const addRegion = async () => {
    if (!validateRegionForm()) return;

    const result = await camMap.addScanRegion({
      ...buildRegionFromForm(),
      regionId: String(Date.now()),
    });
    if (result) {
      refreshRegions();
      clearForm();
      return;
    }
    displayErrorModal({ text: 'Failed to save region to the mapping file.' });
  };

  const updateRegion = async () => {
    if (editingRegionId === null || !validateRegionForm(editingRegionId)) return;

    const result = await camMap.updateScanRegion(editingRegionId, {
      ...buildRegionFromForm(),
      regionId: editingRegionId,
      reportUrl: null,
      masks: [],
      rows: null,
      cols: null,
      tileWidth: null,
      tileHeight: null,
    });
    if (result) {
      dispatch(updatePaths({ 'grid.editingRegionId': null }));
      refreshRegions();
      clearForm();
      return;
    }
    displayErrorModal({ text: 'Failed to update region in the mapping file.' });
  };

  const cancelEdit = () => {
    dispatch(updatePaths({
      'grid.editingRegionId': null,
    }));
    clearForm();
  };

  const listenForScanRegionDone = useCallback((regionId, camera) => {
    if (!socket) {
      return {
        promise: Promise.reject(new Error('no_socket')),
        cancel: () => {},
      };
    }

    let timeoutId;
    let onDone;
    const promise = new Promise((resolve, reject) => {
      onDone = (payload) => {
        if (payload?.camera && payload.camera !== camera) return;
        if (payload?.regionId && payload.regionId !== regionId) return;
        // Production done is also fanned to admins — only settle reference waits.
        if (payload?.isReferenceScan !== true) return;

        clearTimeout(timeoutId);
        socket.off(SCAN_REGION_DONE, onDone);
        resolve(payload);
      };

      timeoutId = setTimeout(() => {
        socket.off(SCAN_REGION_DONE, onDone);
        reject(new Error('scan_region_timeout'));
      }, SCAN_REGION_TIMEOUT_MS);

      socket.on(SCAN_REGION_DONE, onDone);
    });

    return {
      promise,
      cancel: () => {
        clearTimeout(timeoutId);
        if (onDone) socket.off(SCAN_REGION_DONE, onDone);
      },
    };
  }, [socket]);

  const applyReferenceResult = async (camera, region, payload) => {
    if (payload?.error) {
      throw new Error(payload.error || 'Grid scan failed.');
    }
    if (!payload?.reportUrl) {
      throw new Error('Grid scan completed but no report URL was returned.');
    }
    const next = { ...region, reportUrl: payload.reportUrl, ...mosaicDimsFromPayload(payload) };
    const result = await camMap.updateScanRegionForCamera(camera, region.regionId, next);
    if (!result) {
      throw new Error('Failed to save scan results to the mapping file.');
    }
  };

  const runReferenceOnCameraRegion = async (camera, region) => {
    const doneWait = listenForScanRegionDone(region.regionId, camera);
    try {
      await triggerCrowdCount(camera, [region], {
        isReferenceScan: true,
        slugDate,
      });
      const payload = await doneWait.promise;
      await applyReferenceResult(camera, region, payload);
    } catch (error) {
      doneWait.cancel();
      throw error;
    }
  };

  const runRegion = async (region, production = false) => {
    const camera = connectedCamera;
    if (!ensureCameraConnected()) return;
    if (!region?.regionId) {
      displayErrorModal({ text: 'Region is missing a regionId.' });
      return;
    }
    if (runningByCamera[camera] || allCamsMode) return;

    setRunningByCamera((prev) => ({ ...prev, [camera]: region.regionId }));

    try {
      if (production) {
        const result = await triggerCrowdCount(camera, [region]);
        displaySuccessModal({
          text: `Production scan queued (${result.enqueued || 1} region).`,
        });
        return;
      }

      if (!slugDate) {
        displayErrorModal({ text: 'Event slugDate is not loaded yet.' });
        return;
      }
      if (!socket) {
        displayErrorModal({ text: 'Socket is not connected.' });
        return;
      }

      await runReferenceOnCameraRegion(camera, region);
      refreshRegions();
    } catch (error) {
      if (error?.message === 'scan_region_timeout') {
        displayErrorModal({ text: 'Grid scan timed out waiting for ACS.' });
        return;
      }
      const apiError = error?.body?.error || error?.message;
      displayErrorModal({
        text: production
          ? (apiError || 'Failed to enqueue production crowd count scan.')
          : (apiError || 'Grid scan failed to start.'),
      });
    } finally {
      setRunningByCamera((prev) => ({ ...prev, [camera]: null }));
    }
  };

  const runAllRegions = async () => {
    if (!ensureCameraConnected() || anyRunning) return;
    await regions.reduce(async (prev, region) => {
      await prev;
      await runRegion(region);
    }, Promise.resolve());
  };

  const runAllRegionsProduction = async () => {
    const camera = connectedCamera;
    if (!camera || !regions.length || anyRunning) return;

    setRunningByCamera((prev) => ({ ...prev, [camera]: 'all-production' }));
    try {
      const result = await triggerCrowdCount(camera, regions);
      displaySuccessModal({
        text: `Production scan queued for ${result.enqueued} regions.`,
      });
    } catch (error) {
      displayErrorModal({
        text: error?.body?.error || error?.message || 'Failed to enqueue production scans.',
      });
    } finally {
      setRunningByCamera((prev) => ({ ...prev, [camera]: null }));
    }
  };

  const runAllCamsProduction = async () => {
    if (anyRunning) return;

    const confirmed = await confirmationDialog({
      title: 'Run all cameras (production)',
      text: 'Enqueue production crowd-count scans for every camera that has scan regions in mapping? Cameras without ACS / placement are skipped.',
      confirmButtonText: 'Queue all',
    });
    if (!confirmed?.value) return;

    setAllCamsMode('all-cams-production');
    try {
      const result = await triggerCrowdCount();
      if (result?.ok === false) {
        displayErrorModal({
          text: result.error || 'Failed to enqueue production scans for all cameras.',
        });
        return;
      }
      const enqueued = Number(result.enqueued) || 0;
      displaySuccessModal({
        text: `Production scan queued for all cameras (${enqueued} regions).`,
      });
    } catch (error) {
      displayErrorModal({
        text: error?.body?.error || error?.message || 'Failed to enqueue production scans for all cameras.',
      });
    } finally {
      setAllCamsMode(null);
    }
  };

  const runAllCamsPreview = async () => {
    if (anyRunning) return;
    if (!slugDate) {
      displayErrorModal({ text: 'Event slugDate is not loaded yet.' });
      return;
    }
    if (!socket) {
      displayErrorModal({ text: 'Socket is not connected.' });
      return;
    }

    const confirmed = await confirmationDialog({
      title: 'Run all cameras (preview)',
      text: 'Run preview scans for every camera that has scan regions in mapping? This waits for each region and saves report URLs. Cameras run in parallel; regions on the same camera run one at a time.',
      confirmButtonText: 'Run all',
    });
    if (!confirmed?.value) return;

    setAllCamsMode('all-cams-preview');
    try {
      const cams = await listMappingCams();
      const byCamera = {};
      await Promise.all(cams.map(async (camera) => {
        try {
          const uiRegions = uiRegionsFromFile(await fetchCamMapJson(camera));
          if (uiRegions.length) byCamera[camera] = uiRegions;
        } catch (err) {
          console.warn(`Failed to load scan regions for ${camera}`, err);
        }
      }));

      const cameras = Object.keys(byCamera);
      if (!cameras.length) {
        displayErrorModal({ text: 'No count regions in mapping.' });
        return;
      }

      let ok = 0;
      let failed = 0;
      await Promise.all(cameras.map(async (camera) => {
        await byCamera[camera].reduce(async (prev, region) => {
          await prev;
          try {
            await runReferenceOnCameraRegion(camera, region);
            ok += 1;
          } catch (error) {
            failed += 1;
            console.error(`Preview scan failed ${camera}/${region.regionId}`, error);
          }
        }, Promise.resolve());
      }));

      refreshRegions();
      if (failed && !ok) {
        displayErrorModal({ text: `All preview scans failed (${failed}).` });
        return;
      }
      if (failed) {
        displayErrorModal({
          text: `Preview finished with ${ok} saved, ${failed} failed.`,
        });
        return;
      }
      displaySuccessModal({
        text: `Preview scans completed for all cameras (${ok} regions).`,
      });
    } catch (error) {
      displayErrorModal({
        text: error?.body?.error || error?.message || 'Failed to run preview scans for all cameras.',
      });
    } finally {
      setAllCamsMode(null);
    }
  };

  const handleEdit = (region) => {
    dispatch(updatePaths({
      'grid.editingRegionId': region.regionId,
      'grid.regionName': region.name || '',
      'grid.startPoint.pan': region.startPan,
      'grid.startPoint.tilt': region.startTilt,
      'grid.endPoint.pan': region.endPan,
      'grid.endPoint.tilt': region.endTilt,
      'preview.zoom.value': region.zoom * 1000,
    }));
  };

  const handleView = (region) => {
    const nextId = viewingRegionId === region.regionId ? null : region.regionId;
    update('grid.viewingRegionId', nextId);
  };

  const fromMappedSeats = async () => {
    if (!ensureCameraConnected() || anyRunning) return;
    if (!socket) {
      displayErrorModal({ text: 'Socket is not connected.' });
      return;
    }

    const scanZoom = Number(zoom) / 1000;
    if (!inRange(scanZoom, 0, MAX_ZOOM)) {
      displayErrorModal({ text: 'Set a valid camera zoom before generating regions from mapped seats.' });
      return;
    }

    let zoomSteps;
    try {
      zoomSteps = await fetchZoomCalibration(socket, connectedCamera, scanZoom);
    } catch (error) {
      displayErrorModal({
        text: error?.message === 'zoom_calibration_timeout'
          ? 'Timed out waiting for zoom calibration from ACS. Is the camera connected?'
          : (error?.message || 'Failed to get zoom calibration from ACS.'),
      });
      return;
    }

    let proposed;
    try {
      proposed = await camMap.buildScanRegionsFromMappedSeats({ ...zoomSteps, zoom: scanZoom });
    } catch (error) {
      displayErrorModal({
        text: error?.message || 'Failed to read mapped seats for this camera.',
      });
      return;
    }

    if (!proposed.length) {
      displayErrorModal({ text: 'No mapped seats on this camera.' });
      return;
    }

    const names = proposed.map((r) => {
      const tiles = Number(r.tileEstimate);
      return Number.isFinite(tiles)
        ? `${r.name} (~${tiles} tiles)`
        : r.name;
    }).join('\n');
    const overCap = proposed.filter((r) => Number(r.tileEstimate) > MAX_SCAN_TILES);
    if (overCap.length) {
      displayErrorModal({
        text: `Some regions still exceed the ${MAX_SCAN_TILES}-tile API limit after auto-split:\n\n${
          overCap.map((r) => `${r.name} (~${r.tileEstimate} tiles)`).join('\n')
        }\n\nSplit them manually before running production.`,
      });
      return;
    }
    const result = await confirmationDialog({
      title: 'From mapped seats',
      text: regions.length
        ? `Replace ${regions.length} existing region(s) with ${proposed.length} from mapped seats?\n\n${names}`
        : `Save ${proposed.length} region(s) from mapped seats?\n\n${names}`,
      confirmButtonText: regions.length ? 'Replace' : 'Save',
    });
    if (!result.value) return;

    const saved = await camMap.saveScanRegions(proposed);
    if (!saved) {
      displayErrorModal({ text: 'Failed to save regions to the mapping file.' });
      return;
    }
    dispatch(updatePaths({
      ...GRID_FORM_CLEAR_PATHS,
      'grid.editingRegionId': null,
      'grid.viewingRegionId': null,
    }));
    refreshRegions();
  };

  const handleDelete = async (id) => {
    const region = regions.find((r) => r.regionId === id);
    const result = await confirmationDialog({
      title: 'Delete Region',
      text: region?.name
        ? `Are you sure you want to delete "${region.name}"?`
        : 'Are you sure you want to delete this region?',
    });

    if (!result.value) return;

    const deleteResult = await camMap.deleteScanRegion(id);
    if (deleteResult) {
      const updates = { 'grid.regions': camMap.getScanRegionsForUi() };
      if (editingRegionId === id) {
        Object.assign(updates, GRID_FORM_CLEAR_PATHS, { 'grid.editingRegionId': null });
      }
      if (viewingRegionId === id) {
        updates['grid.viewingRegionId'] = null;
      }
      dispatch(updatePaths(updates));
      return;
    }
    displayErrorModal({ text: 'Failed to delete region from the mapping file.' });
  };

  return (
    <div className={style.gridTab}>
      <div className={style.pointsRow}>
        <GridPoint label="Start Point" path="startPoint" disabled={!connectedCamera} />
        <GridPoint label="End Point" path="endPoint" disabled={!connectedCamera} />
      </div>
      <div className={style.block}>
        <div className={`${style.row} ${style.regionNameRow}`}>
          <input
            className={style.input}
            type="text"
            value={regionName}
            onChange={(e) => update('grid.regionName', e.target.value)}
            placeholder="Region name"
          />
          {editingRegionId !== null ? (
            <div className={style.regionActions}>
              <Button
                variant="outline-primary"
                className="small"
                onClick={updateRegion}
                disabled={!connectedCamera}
              >
                Update
              </Button>
              <Button
                variant="outline-secondary"
                className="small"
                onClick={cancelEdit}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="outline-primary"
              className="small"
              onClick={addRegion}
              disabled={!connectedCamera}
            >
              Add Region
            </Button>
          )}
        </div>
      </div>
      <div className={`${style.block} ${style.regionsBlock}`}>
        <div className={style.top}>
          <span>
            {`Regions (${regions.length})`}
          </span>
          <Button
            variant="outline-primary"
            className="small"
            onClick={fromMappedSeats}
            disabled={!connectedCamera || anyRunning}
          >
            From mapped seats
          </Button>
        </div>
        {regions.length === 0 ? (
          <div className={style.row}>
            <span className={style.emptyList}>No regions added yet</span>
          </div>
        ) : (
          <div className={style.regionList}>
            {regions.map((region) => (
              <div
                key={`region-${region.regionId}`}
                className={`${style.regionItem} ${editingRegionId === region.regionId ? style.regionItemEditing : ''} ${viewingRegionId === region.regionId ? style.regionItemViewing : ''}`}
              >
                <RegionSummary region={region} />
                <RegionActionsDropdown
                  region={region}
                  cameraConnected={Boolean(connectedCamera)}
                  viewingRegionId={viewingRegionId}
                  runningRegionId={runningRegionId}
                  busy={anyRunning}
                  onRun={runRegion}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={`${style.block} ${style.runAllBlock}`}>
        <div className={`${style.row} ${style.runAllRow}`}>
          <Button
            variant="outline-primary"
            className="small"
            onClick={runAllRegions}
            disabled={!connectedCamera || regions.length === 0 || anyRunning}
          >
            {runningRegionId ? 'Running…' : 'Run All Regions'}
          </Button>
          <Button
            variant="outline-primary"
            className="small"
            onClick={runAllCamsPreview}
            disabled={anyRunning}
          >
            {allCamsMode === 'all-cams-preview' ? 'Running…' : 'Run All Cams Preview'}
          </Button>
          <Button
            variant="outline-primary"
            className="small"
            onClick={runAllRegionsProduction}
            disabled={!connectedCamera || regions.length === 0 || anyRunning}
          >
            {runningRegionId === 'all-production' ? 'Running…' : 'Run All Production'}
          </Button>
          <Button
            variant="outline-primary"
            className="small"
            onClick={runAllCamsProduction}
            disabled={anyRunning}
          >
            {allCamsMode === 'all-cams-production' ? 'Queuing…' : 'Run All Cams Production'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Grid;
